import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Formik } from "formik";
import * as Yup from "yup";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import useSession from "../utils/useSession";
import { filterActiveMasters } from "../utils/masterFilters";

const LeaveSchema = Yup.object().shape({
  Name: Yup.string().required("Leave name is required"),
  F_FirmMaster: Yup.number().min(1, "Firm is required").required("Firm is required"),
  F_YearMaster: Yup.number().required("Year is required"),
  MaxLimit: Yup.number().required("Max limit is required"),
  LeavePayableLimit: Yup.number().required("Leave payable limit is required"),
  MaxContinousLeave: Yup.number().required("Max continuous leave is required"),
  DaysToCalculateLeaveWithWages: Yup.number().required("Days to calculate leave with wages is required"),
});

const getDaysInMonth = (monthVal) => {
  const m = Number(monthVal);
  if (!m || isNaN(m)) return 31;
  if (m === 2) return 29; // February (up to 29 for leap/annual cycles)
  if ([4, 6, 9, 11].includes(m)) return 30; // April, June, September, November
  return 31; // January, March, May, July, August, October, December
};

const AddEdit_LeaveMaster = () => {
  const { firm: selectedFirm, firmId } = useSession();

  const [state, setState] = useState({
    id: 0,
    formData: {
      F_FirmMaster: firmId || 0,
      F_YearMaster: "",
      Name: "",
      IsYearCarryforward: false,
      MaxLimit: "",
      LeavePayableLimit: "",
      MaxContinousLeave: "",
      DaysToCalculateLeaveWithWages: "",
      CalenderFromMonth: "",
      CalenderFromDay: "",
      CalenderToMonth: "",
      CalenderToDay: "",
    },
    FirmMaster: [],
    YearMaster: [],
    isProgress: false,
  });

  const [existingLeaves, setExistingLeaves] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const API_URL_SAVE = "LeaveMaster/0/token";
  const API_URL_EDIT = `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/Id`;

  useEffect(() => {
    Fn_FillListData(dispatch, setState, "FirmMaster", `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`);
    Fn_FillListData(dispatch, setState, "YearMaster", `${API_WEB_URLS.MASTER}/0/token/YearMaster/Id/0`);

    if (firmId) {
      Fn_FillListData(dispatch, setExistingLeaves, "gridData", `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${firmId}`).catch(() => {});
    }

    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prev) => ({ ...prev, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    } else {
      // New record: pre-fill firm from session
      setState((prev) => ({
        ...prev,
        formData: { ...prev.formData, F_FirmMaster: firmId || 0 },
      }));
    }
  }, [dispatch, location.state, firmId]);

  const handleSubmit = async (values) => {
    try {
      // Check for duplicate leave name for the same firm and year
      const isDuplicate = existingLeaves.some(
        (l) =>
          Number(l.Id) !== Number(state.id) &&
          String(l.F_YearMaster) === String(values.F_YearMaster) &&
          l.Name?.trim().toLowerCase() === values.Name?.trim().toLowerCase()
      );

      if (isDuplicate) {
        toast.error(`Leave "${values.Name}" already exists for this Year!`);
        alert(`Leave "${values.Name}" already exists for this Year!`);
        return;
      }

      const formData = new FormData();
      formData.append("F_FirmMaster", values.F_FirmMaster);
      formData.append("F_YearMaster", values.F_YearMaster);
      formData.append("Name", values.Name);
      formData.append("IsYearCarryforward", values.IsYearCarryforward ? true : false);
      formData.append("MaxLimit", values.MaxLimit);
      formData.append("LeavePayableLimit", values.LeavePayableLimit);
      formData.append("MaxContinousLeave", values.MaxContinousLeave);
      formData.append("DaysToCalculateLeaveWithWages", values.DaysToCalculateLeaveWithWages);
      formData.append("CalenderFromMonth", values.CalenderFromMonth || "");
      formData.append("CalenderFromDay", values.CalenderFromDay || "");
      formData.append("CalenderToMonth", values.CalenderToMonth || "");
      formData.append("CalenderToDay", values.CalenderToDay || "");

      Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: state.id, formData } },
        API_URL_SAVE,
        true,
        "memberid",
        navigate,
        "/LeaveMaster"
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while submitting the form. Please try again.");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Leave Master" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "10px" }}>
              <h4 className="card-title mb-0">Leave Master</h4>
              {selectedFirm?.Name ? (
                <span style={{
                  background: "#1a7f4b",
                  color: "#fff",
                  padding: "4px 14px",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: 600,
                  letterSpacing: "0.3px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}>
                  <i className="fa fa-building" style={{ fontSize: "11px" }} />
                  {selectedFirm.Name}
                </span>
              ) : (
                <span style={{
                  background: "#dc3545",
                  color: "#fff",
                  padding: "4px 14px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}>
                  <i className="fa fa-exclamation-triangle" style={{ fontSize: "11px" }} />
                  Please Select Firm from Dashboard
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="basic-form">
                <Formik
                  initialValues={state.formData}
                  enableReinitialize
                  validationSchema={LeaveSchema}
                  onSubmit={(values, { setSubmitting }) => {
                    setTimeout(() => {
                      handleSubmit(values);
                      setSubmitting(false);
                    }, 400);
                  }}
                >
                  {({ values, errors, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        {/* F_FirmMaster hidden - auto filled from dashboard */}
                        <input type="hidden" name="F_FirmMaster" value={values.F_FirmMaster} onChange={handleChange} />

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Year *</label>
                          <select
                            className="form-control"
                            name="F_YearMaster"
                            value={values.F_YearMaster}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Year --</option>
                            {filterActiveMasters(state.YearMaster, values.F_YearMaster).map((y) => <option key={y.Id} value={y.Id}>{y.Name}</option>)}
                          </select>
                          {errors.F_YearMaster && <div className="text-danger fs-12">{errors.F_YearMaster}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Leave Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="Name"
                            value={values.Name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.Name && <div className="text-danger fs-12">{errors.Name}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Max Limit *</label>
                          <input
                            type="number"
                            className="form-control"
                            name="MaxLimit"
                            value={values.MaxLimit}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.MaxLimit && <div className="text-danger fs-12">{errors.MaxLimit}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Leave Payable Limit *</label>
                          <input
                            type="number"
                            className="form-control"
                            name="LeavePayableLimit"
                            value={values.LeavePayableLimit}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.LeavePayableLimit && <div className="text-danger fs-12">{errors.LeavePayableLimit}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Max Continuous Leave *</label>
                          <input
                            type="number"
                            className="form-control"
                            name="MaxContinousLeave"
                            value={values.MaxContinousLeave}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.MaxContinousLeave && <div className="text-danger fs-12">{errors.MaxContinousLeave}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Days To Calculate Leave With Wages *</label>
                          <input
                            type="number"
                            className="form-control"
                            name="DaysToCalculateLeaveWithWages"
                            value={values.DaysToCalculateLeaveWithWages}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.DaysToCalculateLeaveWithWages && <div className="text-danger fs-12">{errors.DaysToCalculateLeaveWithWages}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Leave Calendar Month (From - To)</label>
                          <div className="d-flex align-items-center gap-1">
                            {/* From Month */}
                            <select
                              className="form-control"
                              name="CalenderFromMonth"
                              value={values.CalenderFromMonth || ""}
                              onChange={(e) => {
                                const newMonth = e.target.value;
                                setFieldValue("CalenderFromMonth", newMonth);
                                const maxD = getDaysInMonth(newMonth);
                                if (Number(values.CalenderFromDay) > maxD) {
                                  setFieldValue("CalenderFromDay", String(maxD).padStart(2, "0"));
                                }
                              }}
                              onBlur={handleBlur}
                              style={{ padding: "6px" }}
                            >
                              <option value="">-- From Month --</option>
                              {[
                                "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                              ].map((month, idx) => {
                                const mVal = String(idx + 1).padStart(2, "0");
                                return <option key={mVal} value={mVal}>{month}</option>;
                              })}
                            </select>

                            {/* From Day */}
                            <select
                              className="form-control"
                              name="CalenderFromDay"
                              value={values.CalenderFromDay || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              style={{ padding: "6px" }}
                            >
                              <option value="">-- Day --</option>
                              {Array.from({ length: getDaysInMonth(values.CalenderFromMonth) }, (_, i) => String(i + 1).padStart(2, "0")).map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </select>

                            <span className="mx-1 text-muted fw-bold">to</span>

                            {/* To Month */}
                            <select
                              className="form-control"
                              name="CalenderToMonth"
                              value={values.CalenderToMonth || ""}
                              onChange={(e) => {
                                const newMonth = e.target.value;
                                setFieldValue("CalenderToMonth", newMonth);
                                const maxD = getDaysInMonth(newMonth);
                                if (Number(values.CalenderToDay) > maxD) {
                                  setFieldValue("CalenderToDay", String(maxD).padStart(2, "0"));
                                }
                              }}
                              onBlur={handleBlur}
                              style={{ padding: "6px" }}
                            >
                              <option value="">-- To Month --</option>
                              {[
                                "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                              ].map((month, idx) => {
                                const mVal = String(idx + 1).padStart(2, "0");
                                return <option key={mVal} value={mVal}>{month}</option>;
                              })}
                            </select>

                            {/* To Day */}
                            <select
                              className="form-control"
                              name="CalenderToDay"
                              value={values.CalenderToDay || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              style={{ padding: "6px" }}
                            >
                              <option value="">-- Day --</option>
                              {Array.from({ length: getDaysInMonth(values.CalenderToMonth) }, (_, i) => String(i + 1).padStart(2, "0")).map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-6 mb-3 d-flex align-items-center mt-2">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="IsYearCarryforward"
                              name="IsYearCarryforward"
                              checked={!!values.IsYearCarryforward}
                              onChange={(e) => setFieldValue("IsYearCarryforward", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="IsYearCarryforward">
                              Year Carryforward
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button type="submit" className="btn btn-primary me-2" disabled={isSubmitting}>
                          {isSubmitting ? "Saving..." : "Save"}
                        </button>
                        {Number(state.id || location.state?.Id || location.state?.id || 0) > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger me-2"
                            onClick={async () => {
                              const delId = Number(state.id || location.state?.Id || location.state?.id || 0);
                              if (!delId) return;
                              if (window.confirm(`Are you sure you want to delete leave master "${values.Name || delId}"?`)) {
                                try {
                                  await Fn_DeleteData(dispatch, () => {}, delId, `${API_WEB_URLS.MASTER}/0/token/LeaveMaster`);
                                  toast.success("Leave master deleted successfully");
                                  navigate("/LeaveMaster");
                                } catch (err) {
                                  console.error("Error deleting leave master:", err);
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger light"
                          onClick={() => navigate("/LeaveMaster")}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_LeaveMaster;
